'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: string;
  label: string;
  value: string;
  description: string;
  icon: string;
  trend: string;
}

export default function TeamStats() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats: Stat[] = [
    {
      id: 'stat_experience',
      label: 'Years Experience',
      value: '6+',
      description: 'Combined team expertise',
      icon: 'AcademicCapIcon',
      trend: '+2 this year',
    },
    {
      id: 'stat_projects',
      label: 'Projects Completed',
      value: '150+',
      description: 'Successful deliveries',
      icon: 'CheckBadgeIcon',
      trend: '+45 this year',
    },
    {
      id: 'stat_clients',
      label: 'Happy Clients',
      value: '85+',
      description: 'Across industries',
      icon: 'UserGroupIcon',
      trend: '+20 this year',
    },
    {
      id: 'stat_satisfaction',
      label: 'Client Satisfaction',
      value: '98%',
      description: 'Average rating',
      icon: 'StarIcon',
      trend: 'Industry leading',
    },
  ];

  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-900 border-y border-zinc-800">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16 animate-on-scroll animate-in">
          <span className="text-xs uppercase tracking-[0.4em] text-blue-400 mb-4 block">
            Our Impact
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Results That <span className="text-gradient-blue">Speak Volumes</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Delivering measurable results and building lasting partnerships with businesses
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={stat.id}
              className={`group bg-gray-950 border border-zinc-800 rounded-xl p-8 hover:border-blue-500 transition-all duration-300 animate-on-scroll animate-in-delay-${
                index + 1
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                  <Icon name={stat.icon as any} size={24} />
                </div>
                <div className="text-xs text-green-400 font-semibold">{stat.trend}</div>
              </div>
              
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-lg font-semibold text-white mb-1">
                {stat.label}
              </div>
              <div className="text-sm text-gray-400">{stat.description}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}